import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card } from '../ui/card';
import { Sun, Compass, Wind } from 'lucide-react';

const Step3WindowsSun = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Windows & Sun Exposure</h2>
        <p className="text-gray-600">Windows and sunlight significantly impact cooling needs</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="numberOfWindows" className="text-base font-medium">
            Number of Windows
          </Label>
          <Input
            id="numberOfWindows"
            type="number"
            min="0"
            max="20"
            value={formData.numberOfWindows}
            onChange={(e) => updateFormData('numberOfWindows', parseInt(e.target.value) || 0)}
            className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="windowType" className="text-base font-medium">
            Window Type
          </Label>
          <Select value={formData.windowType} onValueChange={(value) => updateFormData('windowType', value)}>
            <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
              <SelectValue placeholder="Select window type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single Glazed</SelectItem>
              <SelectItem value="double">Double Glazed</SelectItem>
              <SelectItem value="triple">Triple Glazed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="space-y-3">
        <Label className="text-base font-medium flex items-center">
          <Compass className="w-5 h-5 mr-2 text-blue-700" />
          Primary Window Orientation
        </Label>
        <RadioGroup 
          value={formData.windowOrientation} 
          onValueChange={(value) => updateFormData('windowOrientation', value)}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          {[
            { value: 'north', label: 'North', icon: '↑' },
            { value: 'south', label: 'South', icon: '↓' },
            { value: 'east', label: 'East', icon: '→' },
            { value: 'west', label: 'West', icon: '←' }
          ].map((dir) => (
            <Card 
              key={dir.value}
              className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                formData.windowOrientation === dir.value 
                  ? 'border-blue-700 bg-blue-50 shadow-md' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <label className="flex items-center justify-center p-4 cursor-pointer">
                <RadioGroupItem value={dir.value} id={`dir-${dir.value}`} className="sr-only" />
                <div className="text-center">
                  <div className={`text-3xl mb-1 ${
                    formData.windowOrientation === dir.value ? 'text-blue-700' : 'text-gray-400'
                  }`}>{dir.icon}</div>
                  <span className={`font-medium ${
                    formData.windowOrientation === dir.value ? 'text-blue-900' : 'text-gray-700'
                  }`}>{dir.label}</span>
                </div>
              </label>
            </Card>
          ))}
        </RadioGroup>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="sunExposure" className="text-base font-medium flex items-center">
          <Sun className="w-5 h-5 mr-2 text-yellow-500" />
          Sun Exposure Level
        </Label>
        <Select value={formData.sunExposure} onValueChange={(value) => updateFormData('sunExposure', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select sun exposure" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Direct Sun - Shaded all day</SelectItem>
            <SelectItem value="partial">Partial Sun - 2-4 hours</SelectItem>
            <SelectItem value="full">Full Sun - Most of the day</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="windowShading" className="text-base font-medium">
          Window Shading
        </Label>
        <Select value={formData.windowShading} onValueChange={(value) => updateFormData('windowShading', value)}>
          <SelectTrigger className="h-12 text-lg border-blue-200 focus:border-blue-500 focus:ring-blue-500">
            <SelectValue placeholder="Select window shading" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None - No window coverings</SelectItem>
            <SelectItem value="blinds">Blinds or Curtains</SelectItem>
            <SelectItem value="external">External Shading (awnings, shutters)</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default Step3WindowsSun;